// frontend/src/components/newui/NewTournamentBracket.jsx
import React from 'react';
import { Play, ExternalLink } from 'lucide-react';

const MaskIcon = ({ src, className = "w-5 h-5" }) => (
    <span
        aria-hidden="true"
        className={`inline-block ${className}`}
        style={{
            backgroundColor: "currentColor",
            WebkitMaskImage: `url(${src})`,
            maskImage: `url(${src})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
        }}
    />
);

const MatchCard = ({ match, currentUserId, currentUserTeamId, onStartMatch }) => {
    const isTeamMatch = !!(match.team1Id || match.team2Id);

    const getEntryStyle = (entryId) => {
        if (!entryId) return 'text-white/60';
        if (isTeamMatch && match.winnerTeamId === entryId) return 'text-[#fce503] font-bold';
        if (!isTeamMatch && match.winnerId === entryId) return 'text-[#fce503] font-bold';
        if (match.status === 'COMPLETED') return 'text-red-400 line-through';
        return 'text-white/90';
    };

    const getStatusBadge = () => {
        switch (match.status) {
            case 'READY_CHECK':
                return (
                    <span className="text-xs font-extrabold uppercase bg-yellow-500 text-[#122436] px-3 py-1 rounded-full">
                        Confirmando
                    </span>
                );
            case 'IN_PROGRESS':
                return (
                    <span className="text-xs font-extrabold uppercase bg-blue-500 text-white px-3 py-1 rounded-full">
                        En Juego
                    </span>
                );
            case 'COMPLETED':
                return (
                    <span className="text-xs font-extrabold uppercase bg-green-500 text-white px-3 py-1 rounded-full">
                        Completado
                    </span>
                );
            default:
                return (
                    <span className="text-xs font-extrabold uppercase bg-gray-500 text-white px-3 py-1 rounded-full">
                        Pendiente
                    </span>
                );
        }
    };

    const isUserInMatch = isTeamMatch
        ? currentUserTeamId === match.team1Id || currentUserTeamId === match.team2Id
        : match.player1Id === currentUserId || match.player2Id === currentUserId;

    const showReadyCheckButton = isUserInMatch &&
        match.player1Id && match.player2Id &&
        (match.status === 'PENDING' || match.status === 'READY_CHECK');

    const showContinueMatchButton = isUserInMatch &&
        match.status === 'IN_PROGRESS' &&
        (match.gameUrl || match.gameInstance);

    return (
        <div className={`relative rounded-[18px] overflow-hidden bg-[#122436] ring-2 ring-white
            shadow-[0_12px_40px_rgba(0,0,0,0.35)] p-5 w-72 transition-transform duration-200
            ${isUserInMatch ? 'ring-[#fce503] ring-4' : ''}`}>

            <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-white/70 font-bold uppercase tracking-wide">
                    Ronda {match.round}
                </span>
                {getStatusBadge()}
            </div>

            <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                    isTeamMatch
                        ? (currentUserTeamId === match.team1Id ? 'bg-[#2284DF]/30 ring-1 ring-[#2284DF]' : 'bg-white/5')
                        : (currentUserId === match.player1Id ? 'bg-[#2284DF]/30 ring-1 ring-[#2284DF]' : 'bg-white/5')
                }`}>
                    <span className={`${getEntryStyle(isTeamMatch ? match.team1Id : match.player1Id)} font-semibold`}>
                        {match.player1Name || 'Por definir'}
                    </span>
                    {(isTeamMatch ? match.winnerTeamId === match.team1Id : match.winnerId === match.player1Id) && (
                        <MaskIcon src="/images/icons/estrella.png" className="w-5 h-5 text-[#fce503]" />
                    )}
                </div>

                <div className="flex items-center">
                    <div className="flex-grow border-b border-white/20"></div>
                    <span className="px-3 text-sm text-white/70 font-bold uppercase">VS</span>
                    <div className="flex-grow border-b border-white/20"></div>
                </div>

                <div className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                    isTeamMatch
                        ? (currentUserTeamId === match.team2Id ? 'bg-[#2284DF]/30 ring-1 ring-[#2284DF]' : 'bg-white/5')
                        : (currentUserId === match.player2Id ? 'bg-[#2284DF]/30 ring-1 ring-[#2284DF]' : 'bg-white/5')
                }`}>
                    <span className={`${getEntryStyle(isTeamMatch ? match.team2Id : match.player2Id)} font-semibold`}>
                        {match.player2Name || 'Por definir'}
                    </span>
                    {(isTeamMatch ? match.winnerTeamId === match.team2Id : match.winnerId === match.player2Id) && (
                        <MaskIcon src="/images/icons/estrella.png" className="w-5 h-5 text-[#fce503]" />
                    )}
                </div>
            </div>

            {/* Botones de acción */}
            <div className="mt-4 space-y-2">
                {showReadyCheckButton && (
                    <button
                        onClick={() => onStartMatch(match)}
                        className="w-full h-10 md:h-11 bg-[#2284DF] text-white rounded-xl font-extrabold uppercase tracking-wide
                                 hover:bg-[#2A91F4] transition-colors flex items-center justify-center gap-2
                                 border-2 border-[#1E75C6] text-sm"
                    >
                        <Play className="w-4 h-4" />
                        {match.status === 'READY_CHECK' ? 'Continuar' : 'Iniciar Match'}
                    </button>
                )}

                {showContinueMatchButton && (
                    <a
                        href={match.gameUrl || `${window.location.origin}/games/truco/game/${match.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-10 md:h-11 bg-green-600 text-white rounded-xl font-extrabold uppercase tracking-wide
                                 hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                        <ExternalLink className="w-4 h-4" />
                        Continuar Partida
                    </a>
                )}
            </div>

            {match.startTime && (
                <div className="mt-3 text-xs text-white/60 text-center font-semibold">
                    {new Date(match.startTime).toLocaleString('es-AR', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </div>
            )}
        </div>
    );
};

const NewTournamentBracket = ({ matches, currentUserId, currentUserTeamId, onStartMatch }) => {
    const roundsMap = matches.reduce((acc, match) => {
        if (!acc[match.round]) {
            acc[match.round] = [];
        }
        acc[match.round].push(match);
        return acc;
    }, {});

    const rounds = Object.entries(roundsMap).sort(([a], [b]) => Number(a) - Number(b));

    const getRoundTitle = (round, totalRounds) => {
        if (round === '1') return 'Primera Ronda';
        if (round === totalRounds.toString()) return 'Final';
        if (round === (totalRounds - 1).toString()) return 'Semifinal';
        return `Ronda ${round}`;
    };

    return (
        <div className="overflow-x-auto">
            <div className="min-w-full inline-flex gap-16 p-8">
                {rounds.map(([round, matchesInRound]) => (
                    <div
                        key={round}
                        className="flex flex-col gap-8"
                        style={{
                            marginTop: `${(Math.pow(2, round - 1) - 1) * 2}rem`
                        }}
                    >
                        <div className="mb-4">
                            <h3 className="toon text-2xl text-[#fce503] uppercase">
                                {getRoundTitle(round, rounds.length)}
                            </h3>
                        </div>

                        {matchesInRound.map((match, index) => (
                            <div
                                key={match.id}
                                style={{
                                    marginTop: index === 0 ? 0 : `${Math.pow(2, round - 1) * 4}rem`
                                }}
                            >
                                <MatchCard
                                    match={match}
                                    currentUserId={currentUserId}
                                    currentUserTeamId={currentUserTeamId}
                                    onStartMatch={onStartMatch}
                                />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NewTournamentBracket;
